import { StackScreenProps } from "@react-navigation/stack";
import { AddGiftVoucherRootParamList } from "../../../../types";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Label,
  Page,
  Stack,
  TextField,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import { View } from "react-native-ui-lib";
import { useLazyQuery } from "@apollo/client";
import { GiftVoucher } from "@towersystems/roam-common/lib/generated-types";
import { QUERY_GIFT_VOUCHER_BY_CODE } from "../../../../utilities/gift-voucher/constants";
import { useSaleControllerProvider } from "../../utils/sale-controller";
import Sale from "../../../../utilities/data/model/Sale";

export interface AddGiftVoucherScreenProps
  extends StackScreenProps<
    AddGiftVoucherRootParamList,
    "AddGiftVoucherScreen"
  > {}

export const AddGiftVoucherScreen = ({
  navigation,
}: AddGiftVoucherScreenProps) => {
  const { sale } = useSaleControllerProvider();
  const [value, setValue] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const flashMessagesContext = useFlashMessages();

  const { t } = useTranslation("gift_voucher");

  const [q, { loading }] = useLazyQuery<{
    giftVoucherByCode: GiftVoucher;
  }>(QUERY_GIFT_VOUCHER_BY_CODE);

  const handleOnSubmit = React.useCallback(
    async (sale: Sale, giftVoucherCode: string) => {
      setSubmitting(true);

      try {
        const result = await q({
          variables: {
            code: giftVoucherCode,
          },
        });

        if (result.error?.graphQLErrors || !result.data) {
          throw new Error("Could not redeem gift voucher");
        }

        if (result.data?.giftVoucherByCode.canBeRedeemed !== true) {
          throw new Error("Gift voucher already redeemed");
        }

        await sale.applyGiftVoucher(
          giftVoucherCode,
          result.data?.giftVoucherByCode.amount
        );

        navigation.getParent()?.getParent()?.goBack();
      } catch (err: any) {
        flashMessagesContext.showMessage({
          message: err?.message ?? "Could not redeem gift voucher",
          severity: "error",
        });
      }
      setSubmitting(false);
    },
    []
  );

  const footer = (
    <Stack fill>
      <View flex>
        <Button
          onPress={async () => {
            sale && (await handleOnSubmit(sale, value));
          }}
          label={t("button.add_gift_voucher")}
          isAction
          disabled={!value || submitting || loading}
        ></Button>
      </View>
    </Stack>
  );

  return (
    <Page
      title={t("title.add_gift_voucher_sale")}
      secondaryTitle={t("title.add_gift_voucher_sale_secondary")}
      footer={footer}
    >
      <View>
        <Label label={t("form.gift_voucher_number.label")}></Label>
        <TextField
          placeholder={t("form.gift_voucher_number.placeholder")}
          value={value}
          onChangeText={(v) => setValue(v)}
        />
      </View>
    </Page>
  );
};
